import { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '../context/AuthContext';
import { usePricing } from '../hooks/usePricing';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY, {
  // Disable wallet payment methods to prevent 401 errors before domain verification
  // Re-enable by removing this config after adding domain to Stripe settings
  locale: 'auto'
});

function CheckoutForm({ onClose, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();
  const { proPricing } = usePricing();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cardComplete, setCardComplete] = useState(false);

  // Form fields
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mobile, setMobile] = useState('');

  const formatMobile = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');

    // Format as: 04XX XXX XXX (Australian format)
    if (digits.length <= 4) return digits;
    if (digits.length <= 7) return `${digits.slice(0, 4)} ${digits.slice(4)}`;
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 10)}`;
  };

  const handleMobileChange = (e) => {
    const formatted = formatMobile(e.target.value);
    setMobile(formatted);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) return;

    // Validate form
    if (!firstName.trim() || !lastName.trim() || !mobile.trim()) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create subscription on backend
      const response = await fetch('/api/create-subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          userEmail: user.email,
          firstName,
          lastName,
          mobile: mobile.replace(/\s/g, '') // Remove spaces for storage
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create subscription');
      }

      const { clientSecret } = await response.json();

      // Confirm card payment
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: `${firstName} ${lastName}`,
            email: user.email,
            phone: mobile.replace(/\s/g, '')
          }
        }
      });

      if (confirmError) {
        throw new Error(confirmError.message);
      }

      // Success!
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'Payment failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = firstName.trim() && lastName.trim() && mobile.trim() && cardComplete;

  return (
    <form onSubmit={handleSubmit} className="divide-y divide-[#d2d2d7] dark:divide-[#2c2c2e]">
      {/* Contact Information */}
      <div className="p-6 sm:p-8">
        <h3 className="text-sm font-semibold text-[#1d1d1f] dark:text-white mb-4 tracking-tight">
          Contact Information
        </h3>

        <div className="space-y-4">
          {/* First Name */}
          <div>
            <label className="block text-xs font-medium text-[#86868b] dark:text-[#8e8e93] mb-2">
              First Name
            </label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="John"
              className="w-full px-4 py-3 bg-white dark:bg-[#1c1c1e] border border-[#d2d2d7] dark:border-[#2c2c2e] rounded-lg text-[#1d1d1f] dark:text-white placeholder-[#86868b] dark:placeholder-[#8e8e93] focus:outline-none focus:ring-2 focus:ring-[#06c] focus:border-transparent transition-all"
              required
            />
          </div>

          {/* Last Name */}
          <div>
            <label className="block text-xs font-medium text-[#86868b] dark:text-[#8e8e93] mb-2">
              Last Name
            </label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Smith"
              className="w-full px-4 py-3 bg-white dark:bg-[#1c1c1e] border border-[#d2d2d7] dark:border-[#2c2c2e] rounded-lg text-[#1d1d1f] dark:text-white placeholder-[#86868b] dark:placeholder-[#8e8e93] focus:outline-none focus:ring-2 focus:ring-[#06c] focus:border-transparent transition-all"
              required
            />
          </div>

          {/* Mobile */}
          <div>
            <label className="block text-xs font-medium text-[#86868b] dark:text-[#8e8e93] mb-2">
              Mobile Number
            </label>
            <input
              type="tel"
              value={mobile}
              onChange={handleMobileChange}
              placeholder="04XX XXX XXX"
              maxLength="12"
              className="w-full px-4 py-3 bg-white dark:bg-[#1c1c1e] border border-[#d2d2d7] dark:border-[#2c2c2e] rounded-lg text-[#1d1d1f] dark:text-white placeholder-[#86868b] dark:placeholder-[#8e8e93] focus:outline-none focus:ring-2 focus:ring-[#06c] focus:border-transparent transition-all font-mono"
              required
            />
          </div>
        </div>
      </div>

      {/* Payment Method */}
      <div className="p-6 sm:p-8">
        <h3 className="text-sm font-semibold text-[#1d1d1f] dark:text-white mb-4 tracking-tight">
          Payment Method
        </h3>

        <div className="p-4 bg-[#f5f5f7] dark:bg-[#2c2c2e] border border-[#d2d2d7] dark:border-[#3c3c3e] rounded-lg transition-colors">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#1d1d1f',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
                  '::placeholder': {
                    color: '#86868b'
                  }
                },
                invalid: {
                  color: '#ef4444',
                  iconColor: '#ef4444'
                }
              },
              hidePostalCode: false
            }}
            onChange={(e) => setCardComplete(e.complete)}
          />
        </div>

        <div className="flex items-center gap-2 mt-3">
          <svg className="w-4 h-4 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <p className="text-xs text-[#86868b] dark:text-[#8e8e93]">
            Your payment information is encrypted and secure
          </p>
        </div>
      </div>

      {/* Order Summary */}
      <div className="p-6 sm:p-8 bg-[#f5f5f7] dark:bg-[#1c1c1e] transition-colors">
        <div className="flex items-baseline justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-[#1d1d1f] dark:text-white">
              QuickMeet Premium
            </h3>
            <p className="text-sm text-[#86868b] dark:text-[#8e8e93] mt-1">
              Monthly subscription
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-semibold text-[#1d1d1f] dark:text-white">
              {proPricing?.price || '$15'}
            </div>
            <div className="text-xs text-[#86868b] dark:text-[#8e8e93]">
              {proPricing?.period || 'per user/month'}
            </div>
          </div>
        </div>

        {/* Features List */}
        <div className="space-y-2 mb-6">
          {[
            'Skip all advertisements',
            'Unlimited meeting duration',
            'Priority support',
            'Early access to new features'
          ].map((feature, index) => (
            <div key={index} className="flex items-center gap-2">
              <svg className="w-4 h-4 text-green-600 dark:text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-sm text-[#6e6e73] dark:text-[#8e8e93]">{feature}</span>
            </div>
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex gap-2">
              <svg className="w-5 h-5 text-red-600 dark:text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          </div>
        )}

        {/* CTA Button */}
        <button
          type="submit"
          disabled={loading || !stripe || !isFormValid}
          className={`w-full py-3.5 rounded-lg font-medium text-base transition-all duration-200 ${
            loading || !stripe || !isFormValid
              ? 'bg-[#d2d2d7] dark:bg-[#2c2c2e] text-[#86868b] cursor-not-allowed'
              : 'bg-[#06c] hover:bg-[#0077ed] text-white shadow-sm hover:shadow-md'
          }`}
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Processing...
            </span>
          ) : (
            'Subscribe'
          )}
        </button>

        <p className="text-xs text-center text-[#86868b] dark:text-[#8e8e93] mt-4">
          Cancel anytime. No commitments.
        </p>
      </div>
    </form>
  );
}

export default function StripeCheckout({ onClose, onSuccess }) {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-white dark:bg-[#1c1c1e] rounded-2xl max-w-md w-full overflow-hidden shadow-2xl max-h-[95vh] overflow-y-auto transition-colors">
        {/* Header */}
        <div className="p-6 sm:p-8 border-b border-[#d2d2d7] dark:border-[#2c2c2e] transition-colors">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold text-[#1d1d1f] dark:text-white tracking-tight">
              Checkout
            </h2>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full hover:bg-[#f5f5f7] dark:hover:bg-[#2c2c2e] flex items-center justify-center transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Stripe Elements Wrapper */}
        <Elements stripe={stripePromise}>
          <CheckoutForm onClose={onClose} onSuccess={onSuccess} />
        </Elements>
      </div>
    </div>
  );
}
